const HEX_COLOR_PATTERN = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const RGB_COLOR_PATTERN = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/;

const expandShortHex = (hex: string): string => {
    if (hex.length !== 4) {
        return hex;
    }

    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
};

const channelToHex = (channel: number): string => channel.toString(16).padStart(2, '0');

const rgbChannelsToHex = (r: number, g: number, b: number): string => `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}`;

const parseRgbString = (color: string): string | null => {
    const match = RGB_COLOR_PATTERN.exec(color.trim());
    if (!match) {
        return null;
    }

    return rgbChannelsToHex(Number(match[1]), Number(match[2]), Number(match[3]));
};

const parseCssVariable = (color: string): { name: string; fallback?: string } | null => {
    const match = /^var\(\s*(--[\w-]+)\s*(?:,\s*([^)]+))?\s*\)$/.exec(color.trim());
    return match ? { name: match[1], fallback: match[2]?.trim() } : null;
};

const resolveColorViaDom = (color: string, fallback: string): string => {
    const element = document.createElement('span');
    element.style.color = color;
    document.documentElement.appendChild(element);
    const computed = getComputedStyle(element).color;
    element.remove();

    return parseRgbString(computed) ?? fallback;
};

/** ApexCharts heatmap shading requires #rrggbb — CSS vars may be rgb/oklch or malformed hex from branding. */
export const resolveCssColorToHex = (color: string, fallback: string): string => {
    const normalizedFallback = expandShortHex(HEX_COLOR_PATTERN.test(fallback.trim()) ? fallback.trim() : '#1C3A4A');

    const input = color.trim();
    if (!input) {
        return normalizedFallback;
    }

    if (HEX_COLOR_PATTERN.test(input)) {
        return expandShortHex(input);
    }

    const rgbHex = parseRgbString(input);
    if (rgbHex) {
        return rgbHex;
    }

    if (typeof document === 'undefined') {
        return normalizedFallback;
    }

    const cssVariable = parseCssVariable(input);
    if (cssVariable) {
        const raw = getComputedStyle(document.documentElement).getPropertyValue(cssVariable.name).trim();
        if (raw) {
            return resolveCssColorToHex(raw, normalizedFallback);
        }

        return cssVariable.fallback ? resolveCssColorToHex(cssVariable.fallback, normalizedFallback) : normalizedFallback;
    }

    try {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const context = canvas.getContext('2d');
        if (context) {
            context.fillStyle = '#010203';
            context.fillStyle = input;
            const resolvedFillStyle = context.fillStyle;
            if (resolvedFillStyle === '#010203') {
                throw new Error('Unable to resolve color via canvas');
            }

            if (typeof resolvedFillStyle === 'string' && HEX_COLOR_PATTERN.test(resolvedFillStyle)) {
                return expandShortHex(resolvedFillStyle);
            }

            context.fillRect(0, 0, 1, 1);
            const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
            return rgbChannelsToHex(r, g, b);
        }
    } catch {
        // Fall through to DOM-based resolution.
    }

    try {
        return resolveColorViaDom(input, normalizedFallback);
    } catch {
        return normalizedFallback;
    }
};

export const getThemeColorHex = (cssVariable: string, fallback: string): string => {
    if (typeof document === 'undefined') {
        return expandShortHex(fallback);
    }

    const raw = getComputedStyle(document.documentElement).getPropertyValue(cssVariable).trim();
    return resolveCssColorToHex(raw, fallback);
};
